import { Component } from '@builder.io/sdk';

import sectionFields from 'molecules/Section/config';

export interface ComparisonCard {
  title?: string;
  description?: string;
  why?: string;
  how?: string;
}

const comparisonCardsFields = [
  {
    name: 'eyebrow',
    type: 'string',
    defaultValue: 'Productized and custom',
    helperText: 'Short label shown above the heading (teal, uppercase).',
  },
  {
    name: 'heading',
    type: 'string',
    defaultValue: 'No trade-off between speed and flexibility',
    helperText: 'Main section heading. Supports line breaks with <br /> tags.',
  },
  {
    name: 'whyLabel',
    type: 'string',
    defaultValue: 'WHY',
    helperText: 'Label for the first reason block inside each card.',
  },
  {
    name: 'howLabel',
    type: 'string',
    defaultValue: 'HOW',
    helperText: 'Label for the second reason block inside each card.',
  },
  {
    name: 'leftCard',
    type: 'object',
    helperText: 'Left card (teal). Title, description, and the WHY / HOW copy.',
    defaultValue: {
      title: 'Productized Integrations',
      description:
        'Pre-built, repeatable integrations ship as core features of your product and deploy to your entire customer base.',
      why: 'Building a one-off connection for every customer drains engineering and cannot keep pace with demand. A single reusable build grows your catalog without growing headcount.',
      how: 'Build each integration once and deploy it multi-tenant, with every customer getting their own configuration and credentials. Users activate what they need from a marketplace inside your product.',
    },
    subFields: [
      { name: 'title', type: 'string', helperText: 'Card title.' },
      { name: 'description', type: 'longText', helperText: 'Card intro copy.' },
      { name: 'why', type: 'longText', helperText: 'Copy for the WHY block.' },
      { name: 'how', type: 'longText', helperText: 'Copy for the HOW block.' },
    ],
  },
  {
    name: 'rightCard',
    type: 'object',
    helperText: 'Right card (ocean blue). Title, description, and the WHY / HOW copy.',
    defaultValue: {
      title: 'Custom Integrations',
      description:
        'Tailored integrations serve a single customer’s unique requirements, like a homegrown ERP or a legacy system.',
      why: 'Strategic accounts come with nonstandard requests, and a slow no puts deals and renewals at risk. A fast yes wins the business without touching your roadmap.',
      how: 'Build for exactly one account on the same platform and deploy it straight to their tenant, with the same monitoring and lifecycle tools as your catalog.',
    },
    subFields: [
      { name: 'title', type: 'string', helperText: 'Card title.' },
      { name: 'description', type: 'longText', helperText: 'Card intro copy.' },
      { name: 'why', type: 'longText', helperText: 'Copy for the WHY block.' },
      { name: 'how', type: 'longText', helperText: 'Copy for the HOW block.' },
    ],
  },
  {
    name: 'showBackgroundGlow',
    type: 'boolean',
    defaultValue: true,
    helperText: 'Show the two blurred teal/blue ovals behind the cards (full-bleed, reaching the viewport edges).',
  },
  ...sectionFields,
] as const;

const comparisonCardsConfig = {
  name: 'ComparisonCards',
  inputs: comparisonCardsFields,
} as Component;

export interface ComparisonCardsConfigProps extends GenerateBuilder<typeof comparisonCardsFields> {
  leftCard?: ComparisonCard;
  rightCard?: ComparisonCard;
}

export default comparisonCardsConfig;
